import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import toast from 'react-hot-toast';
import ResponseDetailsModal from './ResponseDetailsModal';
import ResponseStatistics from './ResponseStatistics';
import {
  AppointmentBooking,
  ExportModule,
  ExportResponse,
  buildElternbriefResultsCsv,
  downloadCsv
} from '../utils/elternbriefExport';
import {
  XMarkIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  BellIcon,
  BellSlashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

interface Recipient {
  id: number;
  schueler_unid: string;
  empfaenger_email: string;
  empfaenger_name: string;
  Vorname: string;
  Name: string;
  Klasse: string;
  geoeffnet_am: string | null;
  beantwortet_am: string | null;
  token_gueltig_bis: string;
  gesperrt: boolean;
  erinnerung_gesendet: boolean;
  erinnerung_gesendet_am: string | null;
  antwort_status: 'beantwortet' | 'geoeffnet' | 'ausstehend' | 'gesperrt' | 'abgelaufen';
  reminder_status: 'beantwortet' | 'erinnerung_moeglich' | 'erinnerung_gesendet' | 'geoeffnet' | 'gesperrt' | 'abgelaufen';
  antworten?: ExportResponse[];
  parent_portal_status?: 'active' | 'pending' | 'expired' | 'none';
}

interface RecipientOverviewProps {
  elternbriefId: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  canSendReminders?: boolean;
}

interface Module {
  id: number;
  modul_typ: ExportModule['modul_typ'];
  titel?: string | null;
  position?: number;
  konfiguration?: any;
  fragen?: Array<{
    id: number;
    frage_text: string;
  }>;
}

interface SurveyColumn {
  modulId: number;
  frageId: number;
  frageText: string;
}

const RecipientOverview: React.FC<RecipientOverviewProps> = ({
  elternbriefId,
  isOpen,
  onClose,
  onUpdate,
  canSendReminders = true
}) => {
  const { user } = useAuth();
  const parentPortalStatusQuery = useQuery(
    ['parent-portal-status'],
    async () => {
      const response = await api.get('/auth/parent-portal-status');
      return Boolean(response.data?.data?.enabled);
    },
    { staleTime: 60_000 }
  );
  const parentPortalEnabled = parentPortalStatusQuery.data !== false;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [filteredRecipients, setFilteredRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderText, setReminderText] = useState('');
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [modules, setModules] = useState<Module[]>([]);
  const [appointmentBookings, setAppointmentBookings] = useState<AppointmentBooking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'schueler',
    direction: 'asc'
  });

  useEffect(() => {
    if (isOpen) {
      loadRecipients();
      loadModulesAndBookings();
    }
  }, [isOpen, elternbriefId]);

  useEffect(() => {
    filterRecipients();
  }, [recipients, filter, searchTerm, classFilter]);

  const loadRecipients = async () => {
    try {
      setLoading(true);
      const [detailResponse, exportResponse, invitationStatusResponse] = await Promise.all([
        api.get(`/massenmail/${elternbriefId}/recipients-detailed`),
        api.get(`/massenmail/${elternbriefId}/export-results`),
        api.get(`/massenmail/${elternbriefId}/parent-invitation-status`)
      ]);

      if (detailResponse.data.success) {
        const detailedRecipients = detailResponse.data.data || [];
        const exportRecipients = exportResponse.data?.success ? exportResponse.data.data || [] : [];
        const responsesById = new Map<number, ExportResponse[]>(
          exportRecipients.map((recipient: any) => [recipient.id, recipient.antworten || []])
        );
        const statusById = new Map<number, string>(
          (invitationStatusResponse.data?.data || []).map((item: any) => [item.recipient_id, item.status])
        );

        const merged = detailedRecipients.map((recipient: Recipient) => ({
          ...recipient,
          antworten: responsesById.get(recipient.id) || [],
          parent_portal_status: (statusById.get(recipient.id) || 'none') as Recipient['parent_portal_status']
        }));
        setRecipients(merged);
      }
    } catch (error: any) {
      console.error('Error loading recipients:', error);
      toast.error('Fehler beim Laden der Empfänger');
    } finally {
      setLoading(false);
    }
  };

  const filterRecipients = () => {
    let filtered = [...recipients];

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(recipient => recipient.antwort_status === filter);
    }

    if (classFilter !== 'all') {
      filtered = filtered.filter(recipient => recipient.Klasse === classFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(recipient =>
        recipient.Vorname.toLowerCase().includes(term) ||
        recipient.Name.toLowerCase().includes(term) ||
        recipient.Klasse.toLowerCase().includes(term) ||
        recipient.empfaenger_email.toLowerCase().includes(term)
      );
    }

    setFilteredRecipients(filtered);
  };

  const getSortValue = (recipient: Recipient, key: string) => {
    const lower = (value: string) => value.toLowerCase();
    switch (key) {
      case 'schueler':
        return lower(`${recipient.Name} ${recipient.Vorname}`.trim());
      case 'klasse':
        return lower(recipient.Klasse || '');
      case 'empfaenger':
        return lower(recipient.empfaenger_name || '');
      case 'email':
        return lower(recipient.empfaenger_email || '');
      case 'status':
        return lower(recipient.antwort_status || '');
      case 'erinnerung':
        return lower(getReminderStatusText(recipient));
      case 'geoeffnet':
        return recipient.geoeffnet_am ? new Date(recipient.geoeffnet_am).getTime() : 0;
      case 'gueltig_bis':
        return recipient.token_gueltig_bis ? new Date(recipient.token_gueltig_bis).getTime() : 0;
      default:
        // Handle dynamic survey columns (key format: survey_modulId_frageId)
        if (key.startsWith('survey_')) {
          const parts = key.split('_');
          const modulId = parseInt(parts[1], 10);
          const frageId = parseInt(parts[2], 10);
          const column = surveyColumns.find(c => c.modulId === modulId && c.frageId === frageId);
          if (column) {
            return lower(getSurveyAnswer(recipient, column));
          }
        }
        return '';
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedRecipients = [...filteredRecipients].sort((a, b) => {
    const valueA = getSortValue(a, sortConfig.key);
    const valueB = getSortValue(b, sortConfig.key);
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return (valueA - valueB) * direction;
    }

    return String(valueA).localeCompare(String(valueB), 'de-DE', { numeric: true }) * direction;
  });

  const getSortIndicator = (key: string) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const selectableRecipients = filteredRecipients.filter(canSendReminder);
  const allSelectableSelected =
    selectableRecipients.length > 0 &&
    selectableRecipients.every(recipient => selectedRecipients.has(recipient.id));

  const handleSelectRecipient = (id: number) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecipients(newSelected);
  };

  const handleSelectAll = () => {
    const selectableRecipients = filteredRecipients.filter(canSendReminder);
    const selectableIds = selectableRecipients.map(recipient => recipient.id);
    const allSelectableSelected =
      selectableIds.length > 0 && selectableIds.every(id => selectedRecipients.has(id));

    if (allSelectableSelected) {
      setSelectedRecipients(new Set());
    } else {
      setSelectedRecipients(new Set(selectableIds));
    }
  };

  const handleSendReminder = async () => {
    if (!canSendReminders) {
      toast.error('Keine Berechtigung zum Versenden von Erinnerungen');
      return;
    }
    if (selectedRecipients.size === 0) {
      toast.error('Bitte wählen Sie mindestens einen Empfänger aus');
      return;
    }

    const recipientIds = Array.from(selectedRecipients);
    
    try {
      setSending(true);
      const response = await api.post(`/massenmail/${elternbriefId}/send-reminder`, {
        recipientIds,
        reminderText: reminderText.trim() || undefined
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedRecipients(new Set());
        setReminderText('');
        setShowReminderModal(false);
        loadRecipients(); // Reload to show updated reminder status
        onUpdate();
      }
    } catch (error: any) {
      console.error('Error sending reminders:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Fehler beim Versenden der Erinnerungen';
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'beantwortet':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'geoeffnet':
        return <EyeIcon className="h-5 w-5 text-blue-600" />;
      case 'gesperrt':
        return <BellSlashIcon className="h-5 w-5 text-yellow-600" />;
      case 'abgelaufen':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'beantwortet':
        return 'Beantwortet';
      case 'geoeffnet':
        return 'Geöffnet';
      case 'gesperrt':
        return 'Gesperrt';
      case 'abgelaufen':
        return 'Abgelaufen';
      default:
        return 'Ausstehend';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'beantwortet':
        return 'bg-green-100 text-green-800';
      case 'geoeffnet':
        return 'bg-blue-100 text-blue-800';
      case 'gesperrt':
        return 'bg-yellow-100 text-yellow-800';
      case 'abgelaufen':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getReminderStatusText = (recipient: Recipient) => {
    if (recipient.antwort_status === 'beantwortet') return 'Beantwortet';
    if (recipient.erinnerung_gesendet) return 'Erinnerung gesendet';
    if (recipient.antwort_status === 'geoeffnet') return 'Geöffnet (Erinnerung möglich)';
    if (recipient.reminder_status === 'erinnerung_moeglich') return 'Erinnerung möglich';
    return 'Nicht möglich';
  };

  function canSendReminder(recipient: Recipient) {
    if (!canSendReminders) return false;
    return recipient.reminder_status === 'erinnerung_moeglich' || recipient.reminder_status === 'geoeffnet';
  }

  // Extract all survey questions from modules as dynamic columns
  const getSurveyColumns = (): SurveyColumn[] => {
    const columns: SurveyColumn[] = [];
    const sortedModules = [...modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    
    sortedModules
      .filter(mod => mod.modul_typ === 'umfrage' && mod.fragen && mod.fragen.length > 0)
      .forEach(mod => {
        mod.fragen!.forEach(frage => {
          columns.push({
            modulId: mod.id,
            frageId: frage.id,
            frageText: frage.frage_text
          });
        });
      });
    
    return columns;
  };

  // Get the answer for a specific survey question for a recipient
  const getSurveyAnswer = (recipient: Recipient, column: SurveyColumn): string => {
    const responses = recipient.antworten || [];
    const response = responses.find(r => r.modul_id === column.modulId && r.modul_typ === 'umfrage');
    
    if (!response) return '';
    
    const antwortData = response.antwort_data || {};
    const umfrageAntworten = antwortData.umfrage_antworten || [];
    
    if (Array.isArray(umfrageAntworten)) {
      const entry = umfrageAntworten.find((a: any) => a.frage_id === column.frageId);
      if (!entry) return '';
      
      if (entry.antwort_text) return entry.antwort_text;
      if (Array.isArray(entry.ausgewaehlte_optionen)) {
        return entry.ausgewaehlte_optionen.join(', ');
      }
      if (entry.bewertung) return String(entry.bewertung);
    }
    
    return '';
  };

  const surveyColumns = getSurveyColumns();

  const fetchAppointmentBookings = async (moduleList: Module[]) => {
    const terminModules = moduleList.filter(
      moduleItem => moduleItem.modul_typ === 'terminbuchen' && moduleItem.konfiguration?.appointment_config_id
    );

    if (terminModules.length === 0) return [];

    const bookingResults = await Promise.all(
      terminModules.map(async moduleItem => {
        const configId = moduleItem.konfiguration?.appointment_config_id;
        try {
          const response = await api.get(`/appointments/configs/${configId}/slots`);
          const slots = response.data?.data || [];
          return slots
            .filter((slot: any) => slot.booking_id && slot.booking_status && slot.booking_status !== 'cancelled')
            .map((slot: any) => ({
              configId,
              moduleTitle: moduleItem.titel || 'Terminbuchung',
              start_time: slot.start_time,
              end_time: slot.end_time,
              parent_name: slot.parent_name,
              parent_email: slot.parent_email,
              student_name: slot.student_name,
              booking_status: slot.booking_status,
              notes: slot.notes
            }));
        } catch (error) {
          return [];
        }
      })
    );

    return bookingResults.flat();
  };

  const loadModulesAndBookings = async () => {
    try {
      setLoadingBookings(true);
      const response = await api.get(`/massenmail/${elternbriefId}`);
      if (response.data.success) {
        const loadedModules = response.data.data?.module || [];
        setModules(loadedModules);
        const bookings = await fetchAppointmentBookings(loadedModules);
        setAppointmentBookings(bookings);
      }
    } catch (error: any) {
      console.error('Error loading appointment bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleExportResults = async () => {
    try {
      setExporting(true);
      const [recipientsResponse, briefResponse] = await Promise.all([
        api.get(`/massenmail/${elternbriefId}/export-results`),
        api.get(`/massenmail/${elternbriefId}`)
      ]);

      if (!recipientsResponse.data.success || !briefResponse.data.success) {
        toast.error('Export fehlgeschlagen');
        return;
      }

      const exportRecipients = recipientsResponse.data.data || [];
      const exportModules: Module[] = briefResponse.data.data?.module || [];
      const exportBookings =
        appointmentBookings.length > 0 ? appointmentBookings : await fetchAppointmentBookings(exportModules);

      const csv = buildElternbriefResultsCsv(exportRecipients, exportModules, exportBookings);
      downloadCsv(csv, `elternbrief_${elternbriefId}_ergebnisse.csv`);
      toast.success('Export erfolgreich');
    } catch (error: any) {
      console.error('Error exporting results:', error);
      toast.error('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  const handleSendParentInvitations = async () => {
    try {
      setInviting(true);
      const recipientIds = sortedRecipients
        .filter((recipient) => recipient.parent_portal_status !== 'active')
        .map((recipient) => recipient.id);
      const response = await api.post(`/massenmail/${elternbriefId}/send-parent-invitations`, { recipientIds });
      if (response.data?.success) {
        toast.success(response.data?.message || 'Einladungen versendet');
        await loadRecipients();
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Einladungen konnten nicht versendet werden';
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const classOptions = Array.from(
    new Set(recipients.map(recipient => recipient.Klasse).filter(Boolean))
  ).sort();
  const hasPortalInviteModule = modules.some((moduleItem) => moduleItem.modul_typ === 'elternportal_einladung');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Empfänger-Übersicht ({recipients.length} Empfänger)
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-900">{recipients.length}</div>
                <div className="text-sm text-gray-600">Gesamt</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {recipients.filter(r => r.antwort_status === 'beantwortet').length}
                </div>
                <div className="text-sm text-gray-600">Beantwortet</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {recipients.filter(r => r.antwort_status === 'geoeffnet').length}
                </div>
                <div className="text-sm text-gray-600">Geöffnet</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {recipients.filter(r => r.antwort_status === 'ausstehend').length}
                </div>
                <div className="text-sm text-gray-600">Ausstehend</div>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-600">
                  {recipients.filter(r => r.antwort_status === 'abgelaufen').length}
                </div>
                <div className="text-sm text-gray-600">Abgelaufen</div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter
                </label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Alle</option>
                  <option value="ausstehend">Ausstehend</option>
                  <option value="geoeffnet">Geöffnet</option>
                  <option value="beantwortet">Beantwortet</option>
                  <option value="gesperrt">Gesperrt</option>
                  <option value="abgelaufen">Abgelaufen</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Klasse
                </label>
                <select
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Alle Klassen</option>
                  {classOptions.map(klasse => (
                    <option key={klasse} value={klasse}>
                      {klasse}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suchen
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nach Name oder Email suchen..."
                  />
                </div>
              </div>

              <div className="flex items-end gap-2">
                <button
                  onClick={() => setShowStatistics(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ChartBarIcon className="h-5 w-5" />
                  Statistiken
                </button>

                <button
                  onClick={handleExportResults}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  {exporting ? 'Exportiere...' : 'Export'}
                </button>
                {parentPortalEnabled && !hasPortalInviteModule ? (
                  <button
                    onClick={handleSendParentInvitations}
                    disabled={inviting}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {inviting ? 'Sende...' : 'Zum Elternportal einladen'}
                  </button>
                ) : null}
                {parentPortalEnabled && hasPortalInviteModule ? (
                  <div className="px-3 py-2 rounded-lg bg-cyan-50 text-cyan-800 text-sm border border-cyan-200">
                    Einladungen werden beim Versand automatisch versendet.
                  </div>
                ) : null}
                
                <button
                  onClick={() => setShowReminderModal(true)}
                  disabled={!canSendReminders || selectedRecipients.size === 0 || sending}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <BellIcon className="h-5 w-5" />
                  Erinnerung senden ({selectedRecipients.size})
                </button>
              </div>
            </div>

            {modules.some(moduleItem => moduleItem.modul_typ === 'terminbuchen') && (
              <div className="mb-6 border border-indigo-200 rounded-lg bg-indigo-50">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-indigo-700 font-medium">
                    <CalendarDaysIcon className="h-5 w-5" />
                    Terminbuchungen ({appointmentBookings.length})
                  </div>
                  <button
                    onClick={() => setShowBookings(prev => !prev)}
                    className="text-sm text-indigo-700 hover:text-indigo-900"
                  >
                    {showBookings ? 'Ausblenden' : 'Anzeigen'}
                  </button>
                </div>
                {showBookings && (
                  <div className="border-t border-indigo-200 bg-white">
                    {loadingBookings ? (
                      <div className="p-6 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <p className="mt-2 text-sm text-gray-600">Lade Terminbuchungen...</p>
                      </div>
                    ) : appointmentBookings.length === 0 ? (
                      <div className="p-6 text-center text-sm text-gray-600">
                        Noch keine Terminbuchungen vorhanden.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-indigo-100 text-indigo-900">
                            <tr>
                              <th className="px-4 py-2 text-left">Anlass</th>
                              <th className="px-4 py-2 text-left">Elternteil</th>
                              <th className="px-4 py-2 text-left">Schüler</th>
                              <th className="px-4 py-2 text-left">Termin</th>
                              <th className="px-4 py-2 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {appointmentBookings.map((booking, index) => (
                              <tr key={`${booking.configId}-${index}`} className="border-t border-indigo-100">
                                <td className="px-4 py-2">
                                  {booking.moduleTitle || 'Terminbuchung'}
                                </td>
                                <td className="px-4 py-2">
                                  <div className="font-medium text-gray-900">
                                    {booking.parent_name || '—'}
                                  </div>
                                  <div className="text-gray-500">{booking.parent_email || ''}</div>
                                </td>
                                <td className="px-4 py-2">
                                  {booking.student_name || '—'}
                                </td>
                                <td className="px-4 py-2">
                                  {new Date(booking.start_time).toLocaleString('de-DE')} –{' '}
                                  {new Date(booking.end_time).toLocaleTimeString('de-DE', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-4 py-2 text-indigo-900">
                                  {booking.booking_status || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Recipients Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Laden...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={allSelectableSelected}
                            onChange={handleSelectAll}
                            disabled={selectableRecipients.length === 0}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                          />
                        </th>
                        <th
                          onClick={() => handleSort('schueler')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Schüler</span>
                            <span className="text-gray-400">{getSortIndicator('schueler')}</span>
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('empfaenger')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Empfänger</span>
                            <span className="text-gray-400">{getSortIndicator('empfaenger')}</span>
                          </div>
                        </th>
                        {surveyColumns.map((column) => {
                          const sortKey = `survey_${column.modulId}_${column.frageId}`;
                          return (
                            <th
                              key={sortKey}
                              onClick={() => handleSort(sortKey)}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer select-none min-w-[150px]"
                              title={column.frageText}
                            >
                              <div className="inline-flex items-center gap-1">
                                <span className="line-clamp-2 normal-case">{column.frageText}</span>
                                <span className="text-gray-400 flex-shrink-0">{getSortIndicator(sortKey)}</span>
                              </div>
                            </th>
                          );
                        })}
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          Portal
                        </th>
                        <th
                          onClick={() => handleSort('status')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Status</span>
                            <span className="text-gray-400">{getSortIndicator('status')}</span>
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('erinnerung')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Erinnerung</span>
                            <span className="text-gray-400">{getSortIndicator('erinnerung')}</span>
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('geoeffnet')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Geöffnet</span>
                            <span className="text-gray-400">{getSortIndicator('geoeffnet')}</span>
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('gueltig_bis')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>Gültig bis</span>
                            <span className="text-gray-400">{getSortIndicator('gueltig_bis')}</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedRecipients.map((recipient) => (
                        <tr key={recipient.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedRecipients.has(recipient.id)}
                              onChange={() => handleSelectRecipient(recipient.id)}
                              disabled={!canSendReminder(recipient)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 break-words">
                                {recipient.Vorname} {recipient.Name}
                              </div>
                              <div className="text-sm text-gray-500">
                                Klasse {recipient.Klasse}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 break-words">
                                {recipient.empfaenger_name}
                              </div>
                              <div className="text-sm text-gray-500 break-words">
                                {recipient.empfaenger_email}
                              </div>
                            </div>
                          </td>
                          {surveyColumns.map((column) => {
                            const answer = getSurveyAnswer(recipient, column);
                            return (
                              <td 
                                key={`survey_${column.modulId}_${column.frageId}`} 
                                className="px-4 py-4"
                                title={answer}
                              >
                                {answer ? (
                                  <span className="text-sm text-gray-700 line-clamp-3">
                                    {answer}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              recipient.parent_portal_status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : recipient.parent_portal_status === 'pending'
                                  ? 'bg-blue-100 text-blue-800'
                                  : recipient.parent_portal_status === 'expired'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                              {recipient.parent_portal_status === 'active'
                                ? 'Portal aktiv'
                                : recipient.parent_portal_status === 'pending'
                                  ? 'Einladung offen'
                                  : recipient.parent_portal_status === 'expired'
                                    ? 'Einladung abgelaufen'
                                    : 'Nicht eingeladen'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(recipient.antwort_status)}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(recipient.antwort_status)}`}>
                                {getStatusText(recipient.antwort_status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            <div className="break-words">
                              {getReminderStatusText(recipient)}
                              {recipient.erinnerung_gesendet_am && (
                                <div className="text-xs text-gray-500">
                                  {new Date(recipient.erinnerung_gesendet_am).toLocaleDateString('de-DE')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recipient.geoeffnet_am ? (
                              <div>
                                {new Date(recipient.geoeffnet_am).toLocaleDateString('de-DE')}
                                <div className="text-xs text-gray-500">
                                  {new Date(recipient.geoeffnet_am).toLocaleTimeString('de-DE')}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(recipient.token_gueltig_bis).toLocaleDateString('de-DE')}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            {recipient.beantwortet_am && (
                              <button
                                onClick={() => {
                                  setSelectedRecipientId(recipient.id);
                                  setShowResponseDetails(true);
                                }}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                                title="Antworten ansehen"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                                Details
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {filteredRecipients.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                Keine Empfänger gefunden
              </div>
            )}
          </div>

          {/* Reminder Modal */}
          {showReminderModal && (
            <div className="fixed inset-0 z-60 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowReminderModal(false)} />

                <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="flex items-center gap-3 mb-4">
                      <BellIcon className="h-6 w-6 text-orange-600" />
                      <h3 className="text-lg font-medium text-gray-900">
                        Erinnerung senden
                      </h3>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                      An {selectedRecipients.size} Empfänger wird eine Erinnerung gesendet.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Erinnerungstext (optional)
                      </label>
                      <textarea
                        value={reminderText}
                        onChange={(e) => setReminderText(e.target.value)}
                        rows={4}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Zusätzlicher Text für die Erinnerung..."
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      onClick={handleSendReminder}
                      disabled={sending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? 'Sende...' : 'Erinnerung senden'}
                    </button>
                    <button
                      onClick={() => setShowReminderModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Response Details Modal */}
          {selectedRecipientId && (
            <ResponseDetailsModal
              recipientId={selectedRecipientId}
              elternbriefId={elternbriefId}
              isOpen={showResponseDetails}
              onClose={() => {
                setShowResponseDetails(false);
                setSelectedRecipientId(null);
              }}
            />
          )}

          {/* Statistics Modal */}
          <ResponseStatistics
            elternbriefId={elternbriefId}
            isOpen={showStatistics}
            onClose={() => setShowStatistics(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default RecipientOverview;
